/**
 * MemberAccountModal — identity-account actions for one member (Firebase
 * today; Cloud Identity under AUTH_PROVIDER=google_identity).
 * Shows account state (disabled, providers, MFA, last sign-in) and lets ops
 * disable/enable, send a password reset, or fully delete the auth record
 * (combined with the OrganizationMember row).
 */
import { useState, useEffect } from 'react';
import { api } from '../../api';
import ReasonField from './ReasonField';
import SigninLinkPanel from './SigninLinkPanel';

const PORTAL_APP_CATALOG = [
  { id: 'discoveryCheck', label: 'Intake Engine (Arc)' },
  { id: 'dataBrowser', label: 'Montana DOC Data Browser' },
];

function formatTs(ms) {
  if (!ms) return '—';
  const n = Number(ms);
  if (!Number.isFinite(n)) return '—';
  return new Date(n).toLocaleString();
}

export default function MemberAccountModal({ member, orgId, onClose, onChanged }) {
  const [details, setDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [reason, setReason] = useState('');
  const [confirmEmail, setConfirmEmail] = useState('');
  const [showDelete, setShowDelete] = useState(false);
  const [resetResult, setResetResult] = useState(null);
  const [portalApps, setPortalApps] = useState(null);
  const [portalDraft, setPortalDraft] = useState(null);
  const [portalReason, setPortalReason] = useState('');
  const [portalSaving, setPortalSaving] = useState(false);

  async function load() {
    setLoading(true);
    setError(null);
    try {
      const [acc, portal] = await Promise.all([
        api.get(`/ops_admin/orgs/${orgId}/members/${member.id}/account`),
        api.get(`/ops_admin/orgs/${orgId}/members/${member.id}/portal_apps`),
      ]);
      setDetails(acc.account);
      setPortalApps(portal.apps || []);
      setPortalDraft(portal.apps || []);
    } catch (e) { setError(e.message); } finally { setLoading(false); }
  }

  useEffect(() => { load(); }, [member.id]);

  function togglePortalApp(appId) {
    setPortalDraft(d => {
      const set = new Set(d || []);
      if (set.has(appId)) set.delete(appId); else set.add(appId);
      return Array.from(set);
    });
  }

  async function savePortalApps() {
    if (!portalReason.trim()) { setError('Reason required'); return; }
    setPortalSaving(true); setError(null);
    try {
      const r = await api.put(`/ops_admin/orgs/${orgId}/members/${member.id}/portal_apps`, {
        apps: portalDraft, reason: portalReason,
      });
      setPortalApps(r.apps);
      setPortalDraft(r.apps);
      setPortalReason('');
      onChanged?.();
    } catch (e) { setError(e.message); } finally { setPortalSaving(false); }
  }

  const portalDirty = portalApps && portalDraft && (
    portalApps.length !== portalDraft.length ||
    portalApps.some(a => !portalDraft.includes(a))
  );

  async function toggleDisabled(disabled) {
    if (!reason.trim()) { setError('Reason required'); return; }
    setBusy(true); setError(null);
    try {
      const r = await api.patch(`/ops_admin/orgs/${orgId}/members/${member.id}/account`, { disabled, reason });
      setDetails(r.account);
      setReason('');
      onChanged?.();
    } catch (e) { setError(e.message); } finally { setBusy(false); }
  }

  async function sendReset() {
    setBusy(true); setError(null);
    try {
      const r = await api.post(`/ops_admin/orgs/${orgId}/members/${member.id}/resend_link`);
      setResetResult(r);
    } catch (e) { setError(e.message); } finally { setBusy(false); }
  }

  async function fullDelete() {
    if (!reason.trim()) { setError('Reason required'); return; }
    if (confirmEmail.toLowerCase() !== (member.email || '').toLowerCase()) {
      setError('Email confirmation does not match'); return;
    }
    setBusy(true); setError(null);
    try {
      await api.post(`/ops_admin/orgs/${orgId}/members/${member.id}/delete_account`, {
        reason, confirm_email: confirmEmail,
      });
      onChanged?.();
      onClose();
    } catch (e) { setError(e.message); setBusy(false); }
  }

  if (resetResult?.setup_link) {
    return (
      <div className="modal-overlay">
        <div className="modal">
          <h2>{resetResult.has_password ? 'Password reset' : 'Setup link'} — {resetResult.email}</h2>
          <SigninLinkPanel
            email={resetResult.email}
            setupLink={resetResult.setup_link}
            firebaseUserCreated={false}
            hasPassword={resetResult.has_password}
            identityEmail={resetResult.identity_email}
            oneTimePassword={resetResult.one_time_password}
            authProvider={resetResult.auth_provider}
            onDone={() => { setResetResult(null); load(); }}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="modal-overlay">
      <div className="modal">
        <h2>Account — {member.email}</h2>

        {loading && <p className="loading-text">Loading account...</p>}

        {details && (
          <div className="account-details">
            <div className="info-grid">
              <div className="info-item"><label>Status</label><span>
                {details.disabled
                  ? <span className="badge badge-suspended">Disabled</span>
                  : <span className="badge badge-active">Enabled</span>}
              </span></div>
              {details.identity_email && (
                <div className="info-item"><label>Sign-in username</label><span className="mono">{details.identity_email}</span></div>
              )}
              {'activated' in details && (
                <div className="info-item"><label>Setup</label><span>{details.activated ? 'Complete' : 'Pending first sign-in'}</span></div>
              )}
              <div className="info-item"><label>Email verified</label><span>{details.email_verified ? 'Yes' : 'No'}</span></div>
              <div className="info-item"><label>MFA</label><span>{details.mfa_enrolled ? 'Enrolled' : (details.mfa_enforced ? 'Required at next sign-in' : 'Not enrolled')}</span></div>
              <div className="info-item"><label>Sign-in providers</label><span>{(details.providers || []).join(', ') || 'password (only)'}</span></div>
              <div className="info-item"><label>Last sign-in</label><span>{formatTs(details.last_sign_in)}</span></div>
              <div className="info-item"><label>Account created</label><span>{formatTs(details.created_at)}</span></div>
              <div className="info-item"><label>UID</label><span className="mono">{details.uid}</span></div>
            </div>
          </div>
        )}

        <div className="account-actions">
          <button className="btn btn-sm btn-ghost" onClick={sendReset} disabled={busy}>
            Send password reset
          </button>
        </div>

        {portalDraft && (
          <div className="portal-apps-section">
            <h4>Portal apps</h4>
            <p className="setup-status-detail">
              What this user sees on chargestack.ai under "Your Available Tools."
            </p>
            <div className="portal-apps-list">
              {PORTAL_APP_CATALOG.map(app => (
                <label key={app.id} className="portal-app-row">
                  <input
                    type="checkbox"
                    checked={portalDraft.includes(app.id)}
                    onChange={() => togglePortalApp(app.id)}
                  />
                  <span>{app.label}</span>
                  <span className="mono portal-app-id">{app.id}</span>
                </label>
              ))}
            </div>
            {portalDirty && (
              <div className="portal-apps-save">
                <ReasonField value={portalReason} onChange={setPortalReason} placeholder="Reason..." />
                <div className="button-row">
                  <button className="btn btn-primary" onClick={savePortalApps}
                    disabled={portalSaving || !portalReason.trim()}>
                    {portalSaving ? 'Saving...' : 'Save portal apps'}
                  </button>
                  <button className="btn btn-ghost" onClick={() => { setPortalDraft(portalApps); setPortalReason(''); }}>
                    Reset
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {details && (
          <div className="account-mutate">
            <ReasonField value={reason} onChange={setReason} placeholder="Reason for change..." />
            {error && <p className="error-text">{error}</p>}
            <div className="button-row">
              {details.disabled ? (
                <button className="btn btn-primary" onClick={() => toggleDisabled(false)} disabled={busy || !reason.trim()}>
                  Re-enable account
                </button>
              ) : (
                <button className="btn btn-warning" onClick={() => toggleDisabled(true)} disabled={busy || !reason.trim()}>
                  Disable account
                </button>
              )}
              {!showDelete && (
                <button className="btn btn-danger" onClick={() => setShowDelete(true)} disabled={busy}>
                  Delete account...
                </button>
              )}
            </div>
          </div>
        )}

        {showDelete && (
          <div className="account-delete-confirm">
            <p className="danger-warning">
              This permanently deletes the identity account AND the OrganizationMember row.
              Cannot be undone. Type the email to confirm.
            </p>
            <input
              className="form-input"
              placeholder={member.email}
              value={confirmEmail}
              onChange={e => setConfirmEmail(e.target.value)}
            />
            <div className="button-row">
              <button className="btn btn-danger" onClick={fullDelete}
                disabled={busy || confirmEmail.toLowerCase() !== (member.email || '').toLowerCase() || !reason.trim()}>
                {busy ? 'Deleting...' : 'Permanently delete account'}
              </button>
              <button className="btn btn-ghost" onClick={() => { setShowDelete(false); setConfirmEmail(''); }}>
                Cancel
              </button>
            </div>
          </div>
        )}

        <div className="button-row">
          <button className="btn btn-ghost" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
}
